import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useChat from "../hooks/useChat";
import { supabase } from "../supabase";
import { useNotifications } from "../hooks/useNotifications";


const MessagesPage = () => {
    const { user } = useAuth();
    console.log("user:", user);
    const { conversations, activeConversationId, messages, loading, selectConversation, send, profiles, preferConversation } = useChat();
    const { notifications, markAsRead } = useNotifications();
    const [draft, setDraft] = useState("");
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [employerNames, setEmployerNames] = useState({});
    const [avatarUrls, setAvatarUrls] = useState({}); // profileId -> resolved avatar url
    const [companyLogos, setCompanyLogos] = useState({}); // employerId -> company logo url
    const fetchedEmployerIdsRef = useRef(new Set()); // Track which employer IDs we've already fetched
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [showThreadOnMobile, setShowThreadOnMobile] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const activeConversation = useMemo(() => {
        return conversations?.find((c) => c.id === activeConversationId) || null;
    }, [conversations, activeConversationId]);

    // Filter conversations based on search query
    const filteredConversations = useMemo(() => {
        if (!searchQuery.trim()) {
            return conversations || [];
        }
        
        const query = searchQuery.toLowerCase().trim();
        return (conversations || []).filter((c) => {
            const { name: displayName } = getDisplayForOther(c);
            return displayName.toLowerCase().includes(query);
        });
    }, [conversations, searchQuery, user?.id, profiles, location.pathname, employerNames]);


    const getDisplayForOther = (conversation) => {
        if (!conversation) return { name: "Conversation", initials: "?" };
        const otherId = user?.id === conversation.participant_a ? conversation.participant_b : conversation.participant_a;
        // Get profileID for other conversation participant
        const otherProfile = profiles[otherId] || null;
        // Use profile data
        let otherProfileData = otherProfile || null;
        
        const isEmployeePath = location.pathname.startsWith('/employee/');

        // fetch other users profile details
        if (!otherProfile && otherId && !fetchedEmployerIdsRef.current.has(otherId)) {
            fetchedEmployerIdsRef.current.add(otherId);
            (async () => {
            try {
                // Fetch profile and get a matching internal_job_listings row (employer_id === profileid)
                const { data: profileData, error: profileError } = await supabase
                    .from('users_profiles')
                    .select('*')
                    .eq('profileid', otherId)
                    .single();

                const { data: jobRows, error: jobError } = await supabase
                    .from('internal_job_listings')
                    .select('employer_id, company_name, logo_filename, created_at')
                    .eq('employer_id', otherId)
                    .eq('status', 'published')
                    .order('created_at', { ascending: false })
                    .limit(1);

                // Combine results so existing code can still read `data.companyname` / `data.profile_image_url`
                const fetchError = profileError || jobError ? (profileError || jobError) : null;
                let combined = null;
                if (profileData || (jobRows && jobRows.length > 0)) {
                    combined = { ...(profileData || {}) };
                    const jobRow = jobRows && jobRows.length > 0 ? jobRows[0] : null;
                    // If profile lacks a company name, use the one from the job listing
                    if ((!combined.companyname || combined.companyname.trim() === '') && jobRow?.company_name) {
                        combined.companyname = jobRow.company_name;
                    }
                    // Expose job metadata if needed later
                    if (jobRow) {
                        combined._job_listing = jobRow;
                    }
                }

                // Keep variable names expected by the existing handling below
                const data = combined;
                const error = fetchError;

                if (!error && data) {
                // If the fetched profile contains a company name, cache it for display
                if (data.companyname && data.companyname.trim()) {
                    setEmployerNames(prev => ({ ...prev, [otherId]: data.companyname.trim() }));
                }
                // If the fetched profile contains a profile image path, resolve and cache it
                if (data.profile_image_url) {
                    let resolved = data.profile_image_url;
                    if (!resolved.startsWith('http')) {
                    const { data: urlData } = supabase.storage.from('profile-images').getPublicUrl(resolved);
                    if (urlData?.publicUrl) resolved = urlData.publicUrl;
                    }
                    setAvatarUrls(prev => ({ ...prev, [otherId]: resolved }));
                }
                // Store the fetched profile data in the conversation for later use
                if (conversation) {
                    conversation["_otherProfileData"] = { ...data };
                }
                } else {
                // on error, allow retries later
                fetchedEmployerIdsRef.current.delete(otherId);
                if (error) console.error('Error fetching profile for', otherId, error);
                }
            } catch (err) {
                // on exception, allow retries later
                fetchedEmployerIdsRef.current.delete(otherId);
                console.error('Exception fetching profile for', otherId, err);
            }
            })();
        }
        // Prefer company name for employees viewing employer; fallback to fetched employerNames
        // Check if profile has companyname (from useChat enrichment) or if we fetched it separately
        const companyNameFromProfile = otherProfile?.companyname?.trim() || '';
        const companyNameFromFetch = employerNames[otherId]?.trim() || '';
        const companyName = isEmployeePath
            ? (companyNameFromProfile || companyNameFromFetch || null)
            : (otherProfile?.role === 'Employer' ? (companyNameFromProfile || null) : null);
        const personName = `${otherProfileData?.firstname || ''} ${otherProfileData?.lastname || ''}`.trim();
        const finalName = companyName || personName || 'Conversation';
        const initialsSource = finalName || otherId || "?";
        const initials = (initialsSource.slice(0, 2) || "?").toUpperCase();
        // Attach profile data to conversation for later use (use existing if already set, otherwise use current)
        conversation["_otherProfileData"] = conversation["_otherProfileData"] || otherProfileData;
        return { name: finalName, initials };
    };

    const getSenderDisplayName = (senderId) => {
        if (!senderId) return 'Unknown';
        // If it's the current user, return "You"
        if (senderId === user?.id) {
            return 'You';
        }
        const senderProfile = profiles?.[senderId];
        const isEmployeePath = location.pathname.startsWith('/employee/');
        // Prefer company name for employees viewing employer; fallback to fetched employerNames
        const companyName = isEmployeePath
            ? (senderProfile?.companyname || employerNames[senderId] || null)
            : (senderProfile?.role === 'Employer' ? (senderProfile?.companyname || null) : null);
        const personName = `${senderProfile?.firstname || ''} ${senderProfile?.lastname || ''}`.trim();
        return companyName || personName || 'Unknown';
    };

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        
        const messageDate = new Date(timestamp);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
        
        // Format time (HH:MM)
        const hours = messageDate.getHours();
        const minutes = messageDate.getMinutes();
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // If message is from today, just show time
        if (messageDay.getTime() === today.getTime()) {
            return timeString;
        }
        
        // If message is from yesterday
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (messageDay.getTime() === yesterday.getTime()) {
            return `Yesterday ${timeString}`;
        }
        
        // If message is from this year, show date and time
        if (messageDate.getFullYear() === now.getFullYear()) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${messageDate.getDate()} ${monthNames[messageDate.getMonth()]} ${timeString}`;
        }
        
        // If message is from a different year, show full date
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${messageDate.getDate()} ${monthNames[messageDate.getMonth()]} ${messageDate.getFullYear()} ${timeString}`;
    };

    const cleanHtmlContent = (html) => {
        if (!html || typeof html !== 'string') return '';
        // Remove empty paragraphs and divs, but preserve formatting
        let cleaned = html
            .replace(/<p><\/p>/gi, '')
            .replace(/<div><\/div>/gi, '')
            .replace(/<p><br><\/p>/gi, '')
            .replace(/<div><br><\/div>/gi, '')
            .trim();

        // Fallback text extraction if document isn't available (e.g., tests)
        const getTextContent = () => {
            if (typeof document === 'undefined') {
                return cleaned.replace(/<[^>]+>/g, '').trim();
            }
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cleaned;
            return (tempDiv.innerText || tempDiv.textContent || '').trim();
        };

        if (!getTextContent()) return '';
        return cleaned;
    };

    const parseMessageContent = (rawContent) => {
        if (typeof rawContent === 'string') {
            const trimmed = rawContent.trim();
            const hasHtml = /<[^>]+>/.test(trimmed);
            return { value: trimmed, isHtml: hasHtml };
        }

        if (rawContent === null || rawContent === undefined) {
            return { value: '', isHtml: false };
        }

        // Gracefully handle unexpected content shapes (objects, arrays, etc.)
        try {
            const stringified = typeof rawContent.toString === 'function'
                ? rawContent.toString()
                : JSON.stringify(rawContent);
            return { value: stringified || '', isHtml: false };
        } catch (err) {
            console.error('Unable to render message content:', err, rawContent);
            return { value: '', isHtml: false };
        }
    };

    const uploadAttachments = async (files) => {
        if (!files || files.length === 0) return [];
        if (!user || !activeConversationId) return [];

        const bucket = process.env.REACT_APP_MESSAGES_BUCKET || "message-attachments";
        const uploadedAttachments = [];

        for (const file of files) {
            try {
                const filePath = `${activeConversationId}/${user.id}/${Date.now()}_${file.name}`;
                
                const { error: uploadError } = await supabase.storage
                    .from(bucket)
                    .upload(filePath, file, {
                        upsert: false,
                        contentType: file.type || "application/octet-stream",
                    });

                if (uploadError) {
                    console.error('Error uploading file:', uploadError);
                    continue;
                }

                const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
                const publicUrl = publicData?.publicUrl || "";

                uploadedAttachments.push({
                    name: file.name,
                    url: publicUrl,
                    path: filePath,
                    type: file.type,
                    size: file.size,
                });
            } catch (error) {
                console.error('Error uploading attachment:', error);
            }
        }

        return uploadedAttachments;
    };

    const onSend = async () => {
        if (!editorRef.current) return;
        const textContent = editorRef.current.innerText.trim();
        if (!textContent && attachments.length === 0) return;
        
        // Get HTML content from contentEditable div to preserve formatting
        let htmlContent = editorRef.current.innerHTML.trim();
        
        // Clean up empty HTML tags
        htmlContent = cleanHtmlContent(htmlContent);
        
        // If after cleaning there's no content, don't send
        if (!htmlContent && attachments.length === 0) return;
        
        setUploading(true);
        try {
            // Upload attachments first
            let uploadedAttachments = [];
            if (attachments.length > 0) {
                uploadedAttachments = await uploadAttachments(attachments);
            }
            
            // Send message with attachments
            const attachmentsToSend = uploadedAttachments.length > 0 ? uploadedAttachments : null;
            await send(htmlContent || '', attachmentsToSend);
            
            // Clear editor and attachments
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
            }
            setDraft("");
            setAttachments([]);
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleFormat = (command, value = null) => {
        if (command === 'code') {
            // Custom handling for code formatting
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && editorRef.current) {
                const range = selection.getRangeAt(0);
                const selectedText = range.toString();
                if (selectedText) {
                    const codeElement = document.createElement('code');
                    codeElement.textContent = selectedText;
                    range.deleteContents();
                    range.insertNode(codeElement);
                    // Clear selection
                    selection.removeAllRanges();
                    editorRef.current.focus();
                } else {
                    // If no selection, insert code tags
                    document.execCommand('insertHTML', false, '<code></code>');
                    if (editorRef.current) {
                        editorRef.current.focus();
                    }
                }
            }
        } else {
            document.execCommand(command, false, value);
            if (editorRef.current) {
                editorRef.current.focus();
            }
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        // Validate file sizes (max 10MB per file)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
                return false;
            }
            return true;
        });
        setAttachments(prev => [...prev, ...validFiles]);
        e.target.value = ''; // Reset input
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        // Validate file sizes (max 10MB per file)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
                return false;
            }
            return true;
        });
        setAttachments(prev => [...prev, ...validFiles]);
        e.target.value = ''; // Reset input
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Handle placeholder for contentEditable
    useEffect(() => {
        if (editorRef.current) {
            const updatePlaceholder = () => {
                if (editorRef.current) {
                    if (editorRef.current.innerText.trim() === '') {
                        editorRef.current.setAttribute('data-empty', 'true');
                    } else {
                        editorRef.current.removeAttribute('data-empty');
                    }
                }
            };
            updatePlaceholder();
            const observer = new MutationObserver(updatePlaceholder);
            if (editorRef.current) {
                observer.observe(editorRef.current, { childList: true, subtree: true, characterData: true });
            }
            return () => observer.disconnect();
        }
    }, [activeConversationId]);

    // Cleanup object URLs when attachments change or component unmounts
    useEffect(() => {
        return () => {
            attachments.forEach(file => {
                if (file.type?.startsWith('image/')) {
                    const url = URL.createObjectURL(file);
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [attachments]);

    // Mark message notifications as read when conversation is opened
    useEffect(() => {
        if (!activeConversationId || !user?.id) return;
        
        // Find all unread message notifications for this conversation
        const conversationNotifications = notifications.filter(
            n => n.type === 'message' && 
                 !n.read && 
                 n.data?.conversation_id === activeConversationId
        );
        
        // Mark each notification as read
        conversationNotifications.forEach(notification => {
            markAsRead(notification.id);
        });
    }, [activeConversationId, notifications, markAsRead, user?.id]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current && messagesContainerRef.current) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [messages, activeConversationId]);

    // If navigation provided a specific conversation to open, prefer/select it
    useEffect(() => {
        // Check URL query parameter first, then location state
        const conversationIdFromUrl = searchParams.get('conversation');
        const targetId = conversationIdFromUrl || location.state?.openConversationId;
        if (targetId) {
            // Record preference before conversations load to avoid defaulting to the first thread
            preferConversation(targetId);
            selectConversation(targetId);
            setShowThreadOnMobile(true);
        }
    }, [searchParams, location.state, selectConversation, preferConversation]);

    // Show thread on mobile when conversation is selected, hide list when thread is shown
    useEffect(() => {
        if (activeConversationId) {
            // Check if we're on mobile
            const isMobile = window.innerWidth < 1024;
            if (isMobile) {
                setShowThreadOnMobile(true);
            }
        } else {
            // If no conversation selected, show list on mobile
            const isMobile = window.innerWidth < 1024;
            if (isMobile) {
                setShowThreadOnMobile(false);
            }
        }
    }, [activeConversationId]);

    // Lazy fetch employer company names by employer_id for employee-side view
    // This is a fallback in case useChat didn't enrich the profiles with company names
    // (e.g., when profiles are loaded incrementally, they don't get enriched)
    useEffect(() => {
        const isEmployeePath = location.pathname.startsWith('/employee/');
        if (!isEmployeePath) return;
        if (!conversations || conversations.length === 0) return;
        if (!profiles || Object.keys(profiles).length === 0) return;

        // Reset fetched IDs when conversations change significantly (new conversation added)
        // This ensures we re-check if profiles get updated
        const currentParticipantIds = new Set();
        conversations.forEach(c => {
            const otherId = user?.id === c.participant_a ? c.participant_b : c.participant_a;
            if (otherId) currentParticipantIds.add(otherId);
        });
        
        // Remove IDs from ref that are no longer in conversations
        fetchedEmployerIdsRef.current.forEach(id => {
            if (!currentParticipantIds.has(id)) {
                fetchedEmployerIdsRef.current.delete(id);
            }
        });

        // Check which IDs need fetching
        const idsToFetch = [];
        for (const c of conversations) {
            const otherId = user?.id === c.participant_a ? c.participant_b : c.participant_a;
            if (!otherId) continue;
            
            const prof = profiles[otherId];
            // If profile doesn't exist yet, skip (will be handled when it loads)
            if (!prof) continue;
            
            // Check if profile already has companyname (from useChat enrichment)
            const hasCompanyInProfile = prof?.companyname && prof.companyname.trim() !== '';
            
            // If profile has company name, skip fetching (but remove from ref and clear fetched name if it was there)
            if (hasCompanyInProfile) {
                fetchedEmployerIdsRef.current.delete(otherId);
                // Clear from employerNames if profile now has it (profile takes precedence)
                setEmployerNames(prev => {
                    if (prev[otherId]) {
                        const next = { ...prev };
                        delete next[otherId];
                        return next;
                    }
                    return prev;
                });
                continue;
            }
            
            // Skip if we've already fetched for this ID
            if (fetchedEmployerIdsRef.current.has(otherId)) continue;
            
            // Need to fetch - mark as fetching
            idsToFetch.push(otherId);
            fetchedEmployerIdsRef.current.add(otherId);
        }

        if (idsToFetch.length === 0) return;

        // Fetch company names asynchronously
        (async () => {
            try {
                const { data, error } = await supabase
                    .from('internal_job_listings')
                    .select('employer_id, company_name, created_at')
                    .in('employer_id', idsToFetch)
                    .eq('status', 'published')
                    .order('created_at', { ascending: false });
                    
                if (!error && data && data.length) {
                    setEmployerNames(prev => {
                        const next = { ...prev };
                        let changed = false;
                        for (const row of data) {
                            if (row.employer_id && row.company_name && !next[row.employer_id]) {
                                next[row.employer_id] = row.company_name.trim();
                                changed = true;
                            }
                        }
                        return changed ? next : prev;
                    });
                }
            } catch (err) {
                console.error('Error fetching employer company names:', err);
                // On error, remove from ref so we can retry
                idsToFetch.forEach(id => fetchedEmployerIdsRef.current.delete(id));
            }
        })();
    }, [location.pathname, conversations, profiles, user?.id]);

    // Resolve avatar URLs from profile_image_url (path -> public URL)
    useEffect(() => {
        if (!profiles) return;
        const entries = Object.entries(profiles);
        if (entries.length === 0) return;

        const next = { ...avatarUrls };
        let changed = false;
        for (const [profileId, profile] of entries) {
            const raw = profile?.profile_image_url;
            if (!raw) continue;
            if (next[profileId]) continue; // already resolved
            let resolved = raw;
            if (!raw.startsWith('http')) {
                const { data } = supabase.storage
                    .from('profile-images')
                    .getPublicUrl(raw);
                if (data?.publicUrl) {
                    resolved = data.publicUrl;
                } else {
                    continue;
                }
            }
            next[profileId] = resolved;
            changed = true;
        }
        if (changed) setAvatarUrls(next);
    }, [profiles]);

    // Fetch company logos for employers (similar to company search)
    useEffect(() => {
        const isEmployeePath = location.pathname.startsWith('/employee/');
        
        if (!isEmployeePath) return;
        if (!conversations || conversations.length === 0) return;
        if (!profiles) return;

        // Get all participant IDs from conversations
        const participantIds = new Set();
        for (const c of conversations) {
            const otherId = user?.id === c.participant_a ? c.participant_b : c.participant_a;
            if (otherId) participantIds.add(otherId);
        }

        const participantIdsArray = Array.from(participantIds);

        // Check which participants are employers by looking at job listings
        // This is more reliable than checking the role field which might be undefined
        (async () => {
            const { data: jobListings } = await supabase
                .from('internal_job_listings')
                .select('employer_id, company_name, logo_filename')
                .in('employer_id', participantIdsArray)
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            // Build set of employer IDs (those who have job listings)
            const employerIdsToFetch = new Set();
            const employerLogoFilenames = {};
            
            if (jobListings && jobListings.length > 0) {
                jobListings.forEach(job => {
                    if (job.employer_id) {
                        employerIdsToFetch.add(job.employer_id);
                        if (job.logo_filename && !employerLogoFilenames[job.employer_id]) {
                            employerLogoFilenames[job.employer_id] = job.logo_filename;
                        }
                    }
                });
            }

            // Also check profiles for role='Employer' as fallback
            // CRITICAL: Only use role from database, not inferred from companyname
            // Having companyname doesn't make someone an employer - only role='Employer' does
            participantIdsArray.forEach(otherId => {
                const prof = profiles?.[otherId];
                // Only treat as employer if role is explicitly 'Employer' in database
                if (prof?.role === 'Employer') {
                    employerIdsToFetch.add(otherId);
                }
            });

            const requestIds = Array.from(employerIdsToFetch);
            
            if (requestIds.length === 0) return;

            // Filter out employers we already have logos for
            const idsToFetch = requestIds.filter(id => !companyLogos[id]);
            
            if (idsToFetch.length === 0) return;

            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";

            // Fetch logos from storage for each employer
            const logoPromises = idsToFetch.map(async (employerId) => {
                try {
                    const logoFilename = employerLogoFilenames[employerId];
                    
                    // List files in the employer's storage folder
                    const { data: files, error: listError } = await supabase.storage
                        .from(bucket)
                        .list(employerId, {
                            limit: 100,
                            offset: 0,
                            sortBy: { column: 'created_at', order: 'desc' }
                        });

                    if (listError || !files || files.length === 0) {
                        return { employerId, logoUrl: null };
                    }

                    // Filter for company logo files
                    const logoFiles = files.filter(f => f.name.startsWith('company_logo_'));

                    if (logoFiles.length === 0) {
                        return { employerId, logoUrl: null };
                    }

                    let logoFile = null;

                    // Try to find the specific logo file if we have a filename
                    if (logoFilename) {
                        logoFile = logoFiles.find(f => 
                            f.name.endsWith(`_${logoFilename}`) ||
                            f.name.endsWith(logoFilename) ||
                            f.name.includes(logoFilename)
                        );
                    }

                    // If not found, use the most recent logo file
                    if (!logoFile) {
                        logoFile = logoFiles.sort((a, b) => {
                            const aTime = a.created_at || a.updated_at || '';
                            const bTime = b.created_at || b.updated_at || '';
                            return bTime.localeCompare(aTime);
                        })[0];
                    }

                    if (logoFile) {
                        const logoPath = `${employerId}/${logoFile.name}`;
                        const { data: logoUrlData } = supabase.storage.from(bucket).getPublicUrl(logoPath);
                        if (logoUrlData?.publicUrl) {
                            return { employerId, logoUrl: logoUrlData.publicUrl };
                        }
                    }

                    return { employerId, logoUrl: null };
                } catch (err) {
                    console.error(`Error fetching logo for employer ${employerId}:`, err);
                    return { employerId, logoUrl: null };
                }
            });

            const logoResults = await Promise.all(logoPromises);
            
            const newLogos = {};
            logoResults.forEach(result => {
                if (result && result.logoUrl) {
                    newLogos[result.employerId] = result.logoUrl;
                }
            });
            
            if (Object.keys(newLogos).length > 0) {
                setCompanyLogos(prev => ({ ...prev, ...newLogos }));
            }
        })();
    }, [location.pathname, conversations, profiles, user?.id, companyLogos]);

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .messages-page-container {
                    padding: 0.5rem !important;
                    padding-top: 0.5rem !important;
                    padding-bottom: 0.5rem !important;
                }
                .messages-page-wrapper {
                    border-radius: 0.75rem !important;
                    height: calc(100vh - 4rem) !important;
                }
                .messages-page-layout {
                    flex-direction: column !important;
                    height: 100% !important;
                }
                .messages-conversations-list {
                    display: flex !important;
                    width: 100% !important;
                    border-right: none !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    max-height: none !important;
                    height: 100% !important;
                    overflow-y: auto !important;
                    flex-direction: column !important;
                }
                .messages-conversations-list.mobile-hidden {
                    display: none !important;
                }
                .messages-thread {
                    flex: 1 !important;
                    min-height: 0 !important;
                }
                .messages-thread.mobile-thread-hidden {
                    display: none !important;
                }
                .messages-back-button {
                    display: flex !important;
                }
            }
            @media (min-width: 1024px) {
                .messages-conversations-list.mobile-hidden {
                    display: flex !important;
                }
                .messages-thread.mobile-thread-hidden {
                    display: flex !important;
                }
                .messages-back-button {
                    display: none !important;
                }
            }
                .messages-header {
                    padding: 0.75rem !important;
                }
                .messages-header h3 {
                    font-size: 0.875rem !important;
                }
                .messages-area {
                    padding: 1rem !important;
                    max-height: calc(100vh - 20rem) !important;
                }
                .messages-composer {
                    padding: 0.75rem !important;
                }
                .messages-composer-editor {
                    min-height: 60px !important;
                    max-height: 120px !important;
                    font-size: 0.875rem !important;
                    padding: 0.75rem !important;
                }
                .messages-composer-toolbar {
                    flex-wrap: wrap !important;
                    gap: 0.75rem !important;
                    padding: 0.75rem 1rem !important;
                }
                .messages-composer-toolbar button {
                    padding: 0.5rem !important;
                    font-size: 0.75rem !important;
                    min-height: 36px !important;
                    min-width: 36px !important;
                }
                .messages-composer-send {
                    padding: 0.75rem 1.5rem !important;
                    font-size: 0.875rem !important;
                    min-height: 44px !important;
                    min-width: 80px !important;
                    flex-shrink: 0 !important;
                }
                .messages-search {
                    padding: 0.75rem !important;
                }
                .messages-search input {
                    font-size: 0.875rem !important;
                    padding: 0.5rem 0.5rem 0.5rem 2rem !important;
                }
            }
        `}</style>
        <div className="max-w-[1200px] mx-auto px-4 py-6 messages-page-container">
            <div className="border rounded-2xl overflow-hidden bg-white messages-page-wrapper">
                <div className="flex h-[70vh] messages-page-layout">
                    {/* Conversations list */}
                    <div className={`flex w-80 border-r flex-col messages-conversations-list ${showThreadOnMobile ? 'mobile-hidden' : ''}`}>
                        {/* company name and logo for employers */}
                        <div className="flex items-center gap-3 p-4 border-b bg-white messages-header">
                            {(() => {
                                const me = profiles?.[user?.id] || null;
                                const isEmployerUser =
                                    me?.role === "Employer" ||
                                    Boolean(me?.companyname);
                                const employerLogoUrl = user?.id
                                    ? user?.user_metadata?.company_logo_url
                                    : null;
                                const rawAvatar = me?.profile_image_url;
                                const employeeAvatarUrl =
                                    (user?.id && avatarUrls?.[user.id]) ||
                                    (rawAvatar && rawAvatar.startsWith("http")
                                        ? rawAvatar
                                        : null);
                                const avatarImageSrc =
                                    isEmployerUser && employerLogoUrl
                                        ? employerLogoUrl
                                        : employeeAvatarUrl || "/userIcon.png";
                                const displayName = isEmployerUser
                                    ? me?.companyname || "Employer"
                                    : `${me?.firstname || ""} ${me?.lastname || ""}`.trim() ||
                                      "User";

                                return (
                                    <>
                                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                            <img
                                                src={avatarImageSrc}
                                                alt={displayName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "/userIcon.png";
                                                }}
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 truncate">
                                                {displayName}
                                            </div>
                                            <div className="text-xs text-gray-600 truncate">
                                                {isEmployerUser
                                                    ? "Company"
                                                    : "Job Seeker"}
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        {/* Search temporarily hidden */}
                        {/* <div className="p-3 messages-search">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full rounded-full border border-gray-300 pl-10 pr-4 py-2 text-sm focus:outline-none"
                                    placeholder="Search..."
                                />
                                <svg
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M10 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" />
                                </svg>
                            </div>
                        </div> */}
                        <div className="flex-1 overflow-y-auto">
                            {loading && conversations?.length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">Loading conversations…</div>
                            )}
                            {!loading && conversations?.length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">No conversations yet.</div>
                            )}
                            {!loading && searchQuery.trim() && filteredConversations?.length === 0 && conversations?.length > 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">No conversations found matching "{searchQuery}".</div>
                            )}
                            {filteredConversations?.map((c) => {
                                const { name: displayName, initials } = getDisplayForOther(c);
                                const otherId = user?.id === c.participant_a ? c.participant_b : c.participant_a;
                                const otherProfile = profiles?.[otherId];
                                const isNameReady = Boolean(
                                    (displayName && displayName !== "Conversation") ||
                                        otherProfile?.firstname ||
                                        otherProfile?.lastname ||
                                        otherProfile?.companyname ||
                                        employerNames?.[otherId]
                                );
                                const isEmployeePath = location.pathname.startsWith('/employee/');
                                // Check if this is an employer (by role, companyname, or if we have a logo for them)
                                const isEmployer = otherProfile?.role === 'Employer' || 
                                                   otherProfile?.companyname || 
                                                   companyLogos[otherId];
                                // For employees messaging employers, prefer company logo
                                const companyLogo = isEmployeePath && isEmployer ? companyLogos[otherId] : null;
                                const rawAvatar = otherProfile?.profile_image_url;
                                const resolvedAvatar = avatarUrls[otherId] || (rawAvatar && rawAvatar.startsWith('http') ? rawAvatar : null);
                                // Prefer company logo for employers, fallback to profile avatar, then initials
                                const displayImage = companyLogo || resolvedAvatar;
                                
                                return (
                                    <div
                                        key={c.id}
                                        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer ${activeConversationId === c.id ? "bg-gray-50" : ""}`}
                                        onClick={() => {
                                            selectConversation(c.id);
                                            setShowThreadOnMobile(true);
                                        }}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                            {!isNameReady ? (
                                                <div className="w-9 h-9 bg-gray-200 animate-pulse rounded-full" />
                                            ) : displayImage ? (
                                                <img
                                                    src={displayImage}
                                                    alt={displayName}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.src = '/userIcon.png';
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-xs text-gray-600">
                                                    {initials}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {!isNameReady ? (
                                                <div className="space-y-1">
                                                    <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                                                    <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {displayName}
                                                    </div>
                                            {/* Optionally show preview here later */}
                                                    {c._otherProfileData?.role !==
                                                        'Employee' && (
                                                        <div className="text-xs text-gray-600">
                                                            {`${c._otherProfileData?.firstname || ''} ${c._otherProfileData?.lastname || ''}`.trim() ||
                                                                'No headline available'}
                                                        </div>
                                                    )}
                                                    {c._otherProfileData?.role ===
                                                        'Employee' && (
                                                        <div className="text-xs text-gray-600">
                                                            Job Seeker
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Thread */}
                    <div className={`flex-1 flex flex-col messages-thread ${!showThreadOnMobile ? 'mobile-thread-hidden' : ''}`}>
                        {/* Header */}
                        <div className="flex items-center gap-3 p-4 border-b bg-white messages-header">
                            <button
                                className="messages-back-button md:hidden mr-2 p-2 hover:bg-gray-100 rounded-full"
                                onClick={() => setShowThreadOnMobile(false)}
                                aria-label="Back to conversations"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                {(() => {
                                    const otherId = user?.id === activeConversation?.participant_a ? activeConversation?.participant_b : activeConversation?.participant_a;
                                    const otherProfile = profiles?.[otherId];
                                    const isEmployeePath = location.pathname.startsWith('/employee/');
                                    // Check if this is an employer (by role, companyname, or if we have a logo for them)
                                    const isEmployer = otherProfile?.role === 'Employer' || 
                                                       otherProfile?.companyname || 
                                                       companyLogos[otherId];
                                    // For employees messaging employers, prefer company logo
                                    const companyLogo = isEmployeePath && isEmployer ? companyLogos[otherId] : null;
                                    const rawAvatar = otherProfile?.profile_image_url;
                                    const resolvedAvatar = avatarUrls[otherId] || (rawAvatar && rawAvatar.startsWith('http') ? rawAvatar : null);
                                    const { initials } = getDisplayForOther(activeConversation);
                                    // Prefer company logo for employers, fallback to profile avatar, then initials
                                    const displayImage = companyLogo || resolvedAvatar;
                                    
                                    if (displayImage) {
                                        return <img src={displayImage} alt="Avatar" className="w-full h-full object-cover" onError={(e) => { 
                                            e.currentTarget.src = '/userIcon.png'; 
                                        }} />;
                                    } else {
                                        return <span className="text-xs text-gray-600">{initials}</span>;
                                    }
                                })()}
                            </div>
                            <div>
                                {(() => {
                                    const otherId = user?.id === activeConversation?.participant_a ? activeConversation?.participant_b : activeConversation?.participant_a;
                                    const { name } = getDisplayForOther(activeConversation);
                                    return (
                                        <>
                                            <div className="text-sm font-semibold">{activeConversationId ? name : "Select a conversation"}</div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Messages area */}
                        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 bg-white messages-area">
                            {loading && !messages?.length && (
                                <div className="text-sm text-gray-500">Loading messages…</div>
                            )}
                            {!loading && activeConversationId && messages?.length === 0 && (
                                <div className="text-sm text-gray-500">No messages yet. Say hello!</div>
                            )}
                            {messages?.map((m) => {
                                const isMine = m.sender_id === user?.id;
                                const { value: messageValue, isHtml } = parseMessageContent(m.content);
                                
                                // Parse attachments - handle JSON string, array, or object
                                let messageAttachments = [];
                                if (m.attachments) {
                                    if (typeof m.attachments === 'string') {
                                        try {
                                            const parsed = JSON.parse(m.attachments);
                                            messageAttachments = Array.isArray(parsed) ? parsed : [parsed];
                                        } catch {
                                            messageAttachments = [];
                                        }
                                    } else if (Array.isArray(m.attachments)) {
                                        messageAttachments = m.attachments;
                                    } else if (typeof m.attachments === 'object' && m.attachments !== null) {
                                        // Handle JSONB objects from Supabase
                                        messageAttachments = Array.isArray(m.attachments) ? m.attachments : [m.attachments];
                                    }
                                }
                                
                                const senderName = getSenderDisplayName(m.sender_id);
                                
                                return (
                                    <div key={m.id} className={`mb-3 flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                                        {/* Sender name */}
                                        <div className={`text-[10px] text-gray-500 mb-1 px-1 ${isMine ? 'text-right' : 'text-left'}`}>
                                            {senderName}
                                        </div>
                                        <div
                                            className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                                                isMine 
                                                    ? 'rounded-br-md bg-black text-white' 
                                                    : 'rounded-bl-md bg-gray-100 text-gray-900'
                                            } ${isHtml ? '' : 'whitespace-pre-wrap'}`}
                                        >
                                            {messageValue && (
                                                isHtml ? (
                                                    <div 
                                                        dangerouslySetInnerHTML={{ __html: messageValue }}
                                                        style={{ wordBreak: 'break-word' }}
                                                    />
                                                ) : (
                                                    messageValue
                                                )
                                            )}
                                            
                                            {/* Display attachments */}
                                            {messageAttachments.length > 0 && (
                                                <div className={`space-y-2 ${messageValue ? 'mt-2' : ''}`}>
                                                    {messageAttachments.map((att, idx) => {
                                                        if (!att || !att.url) {
                                                            return null;
                                                        }
                                                        
                                                        const isImage = att.type?.startsWith('image/') || 
                                                                      att.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                                                        return (
                                                            <div key={idx} className="flex items-start gap-2">
                                                                {isImage ? (
                                                                    <div className="relative">
                                                                        <img 
                                                                            src={att.url} 
                                                                            alt={att.name || 'Attachment'} 
                                                                            className="max-w-full max-h-64 rounded-lg object-contain cursor-pointer border border-gray-200"
                                                                            onClick={() => window.open(att.url, '_blank')}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                const fallback = e.target.nextElementSibling;
                                                                                if (fallback) fallback.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                        <div className="hidden text-xs text-gray-500 mt-1">
                                                                            Failed to load image
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <a
                                                                        href={att.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 underline text-sm"
                                                                    >
                                                                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                        </svg>
                                                                        <span className="text-xs">{att.name || 'Download'}</span>
                                                                        {att.size && (
                                                                            <span className="text-xs text-gray-500">
                                                                                ({(att.size / 1024).toFixed(1)} KB)
                                                                            </span>
                                                                        )}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        {/* Timestamp */}
                                        <div className={`text-[10px] text-gray-400 mt-1 px-1 ${isMine ? 'text-right' : 'text-left'}`}>
                                            {formatMessageTime(m.created_at)}
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Composer */}
                        <div className="border-t bg-white messages-composer">
                            <div className="p-4">
                                <div className="relative">
                                    <div
                                        ref={editorRef}
                                        contentEditable={!!activeConversationId}
                                        className="w-full min-h-[80px] max-h-[200px] overflow-y-auto resize-none border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 messages-composer-editor"
                                        onInput={(e) => {
                                            const text = e.target.innerText;
                                            setDraft(text);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                onSend();
                                            }
                                        }}
                                        style={{
                                            minHeight: '80px',
                                            maxHeight: '200px',
                                        }}
                                        data-placeholder="Type your message here..."
                                    />
                                    {!draft && (
                                        <div className="absolute top-3 left-3 text-gray-400 text-sm pointer-events-none">
                                            Type your message here...
                                        </div>
                                    )}
                                </div>
                                {attachments.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                        {attachments.map((file, index) => {
                                            const isImage = file.type?.startsWith('image/');
                                            const previewUrl = isImage ? URL.createObjectURL(file) : null;
                                            return (
                                                <div key={index} className="flex items-start gap-2 bg-gray-50 rounded-lg p-2">
                                                    {previewUrl && (
                                                        <img 
                                                            src={previewUrl} 
                                                            alt={file.name} 
                                                            className="w-16 h-16 object-cover rounded"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium text-gray-900 truncate">{file.name}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {(file.size / 1024).toFixed(1)} KB
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            if (previewUrl) URL.revokeObjectURL(previewUrl);
                                                            removeAttachment(index);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 text-lg leading-none"
                                                        aria-label="Remove attachment"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    multiple
                                    onChange={handleFileUpload}
                                />
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageUpload}
                                />
                            </div>
                            <div className="px-4 pb-4 flex items-center gap-3 messages-composer-toolbar">
                                <div className="flex items-center gap-3 text-gray-600">
                                    <button
                                        type="button"
                                        className="p-1.5 hover:bg-gray-100 rounded font-bold"
                                        aria-label="Bold"
                                        onClick={() => handleFormat('bold')}
                                        disabled={!activeConversationId}
                                    >
                                        B
                                    </button>
                                    <button
                                        type="button"
                                        className="p-1.5 hover:bg-gray-100 rounded italic"
                                        aria-label="Italic"
                                        onClick={() => handleFormat('italic')}
                                        disabled={!activeConversationId}
                                    >
                                        I
                                    </button>
                                    <button
                                        type="button"
                                        className="p-1.5 hover:bg-gray-100 rounded underline"
                                        aria-label="Underline"
                                        onClick={() => handleFormat('underline')}
                                        disabled={!activeConversationId}
                                    >
                                        U
                                    </button>
                                </div>
                                <button
                                    className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm disabled:opacity-50 messages-composer-send"
                                    onClick={onSend}
                                    disabled={!activeConversationId || (!draft.trim() && attachments.length === 0) || uploading}
                                >
                                    {uploading ? 'Sending...' : 'Send'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default MessagesPage;


